import { GridColDef, GridRowParams } from "@mui/x-data-grid";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import MainApiService from "../../../../../services/mainApi.service";
import {
  AutoCompleteUncontrolled,
  ISelectType,
  OverflowTooltip,
  Table,
} from "@4uhub/lib4uhub";
import { ITicketAutoFinishRules } from "../../../../../models/ticket-auto-finish-rule";
import { Chip, Stack, Typography } from "@mui/material";
import { format } from "date-fns";
import { ptBR, enUS } from "date-fns/locale";
import { useMemo, useState } from "react";

const TICKET_AUTO_FINISH_RULE_ROUTE =
  process.env.REACT_APP_TICKETS + "/api/v1/TicketAutoFinishRule/";
const ticketAutoFinishRuleService = new MainApiService<ITicketAutoFinishRules>(
  TICKET_AUTO_FINISH_RULE_ROUTE
);

const TICKET_CHANNEL_ROUTE =
  process.env.REACT_APP_TICKETS + "/api/v1/TicketChannels/";
const ticketChannelService = new MainApiService<ISelectType>(
  TICKET_CHANNEL_ROUTE
);

const translationPath = "page.settings.crm.ticketAutoFinishRules.";

const TicketAutoFinishRulesPage = () => {
  const navigate = useNavigate();
  const {
    t,
    i18n: { language },
  } = useTranslation();
  const { eI } = useParams();

  const [selectedTicketChannel, setSelectedTicketChannel] =
    useState<ISelectType | null>(null);

  const columns: GridColDef<ITicketAutoFinishRules>[] = [
    {
      field: "ticketType",
      headerName: t(translationPath + "ticket_type") || "Ticket type",
      renderCell: (params) => {
        if (!params.value) return "-";
        return params.value.name;
      },
    },
    {
      field: "ticketChannel",
      headerName: t(translationPath + "ticket_channel") || "Board",
      renderCell: (params) => {
        if (!params.value) return "-";
        return params.value.name;
      },
    },
    {
      field: "ticketChannelStep",
      headerName: t(translationPath + "ticket_channel_step") || "Board list",
      renderCell: (params) => {
        if (!params.value) return "-";
        return params.value.name;
      },
    },
    {
      field: "message",
      headerName: t(translationPath + "message") || "Message",
      renderCell: (params) => {
        if (!params.value) return "-";
        return (
          <OverflowTooltip
            text={params.value}
            tooltip={params.value}
            variant="body2"
          />
        );
      },
    },
    {
      field: "minutes",
      headerName: t(translationPath + "minutes") || "Minutes",
      maxWidth: 100,
    },
    {
      field: "initialDate",
      headerName: t(translationPath + "initial_date") || "Initial date",
      maxWidth: 150,
      renderCell: (params) => {
        const date = new Date(params.value);
        return params.value
          ? format(date, "P", {
              locale: language === "pt-BR" ? ptBR : enUS,
            })
          : "-";
      },
    },
    {
      field: "endDate",
      headerName: t(translationPath + "end_date") || "End date",
      maxWidth: 150,
      renderCell: (params) => {
        const date = new Date(params.value);
        return params.value
          ? format(date, "P", {
              locale: language === "pt-BR" ? ptBR : enUS,
            })
          : "-";
      },
    },
    {
      field: "finish",
      headerName: t(translationPath + "finish_type") || "Finishing Type",
      maxWidth: 170,
      renderCell: ({ row: { finish, endOfDay } }) => {
        const label = finish
          ? t(translationPath + "finish_by_time")
          : endOfDay
          ? t(translationPath + "finish_end_of_day")
          : t(translationPath + "finish_not_selected");
        return (
          <Typography variant="body2" title={label}>
            {label}
          </Typography>
        );
      },
    },
    {
      field: "enable",
      headerName: t(translationPath + "enable") || "Active",
      maxWidth: 70,
      renderCell: ({ row: { finish, endOfDay } }) => {
        const enabled = finish || endOfDay;
        return (
          <Chip
            size="small"
            label={
              enabled
                ? t(translationPath + "active")
                : t(translationPath + "inactive")
            }
            color={enabled ? "success" : "error"}
          />
        );
      },
    },
  ];

  const handleAdd = () => {
    navigate("new");
  };

  const handleRowClick = (params: GridRowParams<ITicketAutoFinishRules>) => {
    navigate(`${params.id.toString()}/edit`);
  };

  const props = useMemo(() => {
    return {
      TicketChannelId: selectedTicketChannel
        ? selectedTicketChannel.id
        : undefined,
    };
  }, [selectedTicketChannel]);

  return (
    <Table<ITicketAutoFinishRules>
      showDefaultMacroFilters={false}
      service={ticketAutoFinishRuleService}
      columns={columns}
      title={t(translationPath + "ticket_auto_finish_rules")}
      searchInput
      onAdd={handleAdd}
      onRowClick={handleRowClick}
      backButton
      serviceProps={props}
      macroFilters={() => (
        <Stack width={300}>
          <AutoCompleteUncontrolled
            label={t(translationPath + "ticket_channel") || "Ticket Channel"}
            onValueSelected={setSelectedTicketChannel}
            value={selectedTicketChannel}
            size="small"
            fullWidth
            service={ticketChannelService.list}
          />
        </Stack>
      )}
      backButtonUrl={`/e/${eI}/dashboard/settings`}
      searchInputPlaceHolder={
        t(translationPath + "search") || "Search for a ticket auto finish rule"
      }
      addButtonLabel={
        t(translationPath + "add_ticket_auto_finish_rule") ||
        "Add Ticket Service Area"
      }
      confirmOptions={{
        title: t(translationPath + "confirm.title"),
        description: t(translationPath + "confirm.description"),
        confirmationButtonProps: {
          variant: "contained",
        },
      }}
    />
  );
};

export default TicketAutoFinishRulesPage;
